import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { EyeIcon } from "@heroicons/react/24/outline";
import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from "../../Catalyst/table";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "../../Catalyst/dropdown";
import { getUserStatusDisplay, isDimmedStatus } from "../../common/userStatus";
import { ROLE_DISPLAY } from "../GlobalSearch/utils";

// Row shape of GET /master-data/users (v1, snake_case). Only the fields this
// table reads are typed; the endpoint returns more.
export interface OrgUser {
  id: string;
  first: string | null;
  last: string | null;
  email: string;
  user_role: string;
  user_status: string | null;
  user_status_date: string | null;
  // Not returned by the API today; rendered as "–" until it is (see ADM-5 notes).
  session_count?: number | null;
}

const getDisplayName = (user: OrgUser) =>
  [user.first, user.last].filter(Boolean).join(" ").trim() || user.email;

interface OrgUsersTableProps {
  users: OrgUser[];
  onViewActivity: (user: OrgUser) => void;
}

const OrgUsersTable = ({ users, onViewActivity }: OrgUsersTableProps) => {
  return (
    <Table>
      <TableHead>
        <TableRow className="bg-[#F9FAFB] border border-x-[#EAECF0]">
          <TableHeader className="w-[34%]">Name</TableHeader>
          <TableHeader className="w-[18%]">Role</TableHeader>
          <TableHeader className="w-[26%]">Status</TableHeader>
          <TableHeader className="w-[15%]">Number of Sessions</TableHeader>
          <TableHeader className="w-[7%] text-right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((user) => {
          const status = getUserStatusDisplay(user.user_status, user.user_status_date);
          const isDimmed = isDimmedStatus(user.user_status);
          return (
            <TableRow key={user.id}>
              <TableCell className={`font-medium text-texthead ${isDimmed ? "opacity-50" : ""}`}>
                {getDisplayName(user)}
              </TableCell>
              <TableCell className="text-texthead">
                {ROLE_DISPLAY[user.user_role] ?? user.user_role}
              </TableCell>
              <TableCell>
                <span className={`inline-flex items-center gap-2 text-sm font-medium ${status.textClass}`}>
                  {status.text}
                  <span className={`h-2 w-2 rounded-full ${status.dotClass}`} aria-hidden="true" />
                </span>
              </TableCell>
              <TableCell className="text-texthead">
                {typeof user.session_count === "number" ? user.session_count : "–"}
              </TableCell>
              <TableCell className="text-right">
                <Dropdown>
                  <DropdownButton outline aria-label={`Actions for ${getDisplayName(user)}`}>
                    <EllipsisHorizontalIcon />
                  </DropdownButton>
                  <DropdownMenu anchor="bottom end">
                    <DropdownItem onClick={() => onViewActivity(user)}>
                      <EyeIcon />
                      <DropdownLabel>View Activity</DropdownLabel>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default OrgUsersTable;
